import { ErrorScreen } from "@/components/ErrorScreen";
import { Toaster } from "@/components/ui/toaster";
import { FpApiError } from "@/lib/api/errors";
import { redirectToAuth } from "@/lib/auth";
import {
  getProfileQueryOptions,
  listProjectsQueryOptions,
} from "@/lib/hooks/useProjects";
import { captureException } from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import React, { useEffect } from "react";
import type { UserSelect } from "../../db/schema";

// Define type for our auth state - just the user
export interface AuthState {
  user?: UserSelect;
}

// Define the context we'll use with the router
export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthState;
}

// Create the root route with our context type
export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,

  validateSearch: (search: Record<string, unknown>) => {
    const result: { tab?: string } = {};
    if (typeof search.tab === "string") {
      result.tab = search.tab;
    }
    return result;
  },

  // Load projects data for non-auth routes
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.user) {
      return { projects: [] };
    }

    try {
      const result = await queryClient.ensureQueryData(
        listProjectsQueryOptions(),
      );
      return {
        projects: result.data.projects,
      };
    } catch (error) {
      // If projects loading fails, continue without them
      return { projects: [] };
    }
  },

  // Centralized authentication check at the root level
  beforeLoad: async ({ context, location }) => {
    if (context.auth.user) {
      return;
    }

    try {
      const userData = await context.queryClient.ensureQueryData(
        getProfileQueryOptions(),
      );

      // Set user in context if successful
      if (userData) {
        context.auth.user = userData.data;
      }
    } catch (error) {
      if (error instanceof FpApiError && error.statusCode === 401) {
        // Don't redirect if we're already on the auth page
        if (
          !location.pathname.startsWith("/auth") &&
          !location.pathname.startsWith("/waitinglist")
        ) {
          redirectToAuth(location.href);
        }
      }
    }
  },

  // Global error boundary for handling auth and other errors
  errorComponent: ({ error }: { error: unknown }) => {
    useEffect(() => {
      captureException(error);
    }, [error]);

    // If this is a redirect response, let the router handle it
    if (error instanceof Response && error.status === 302) {
      return null;
    }

    // For all other errors, show the error screen
    return (
      <ErrorScreen
        error={error as Error}
        title="Error loading Fiberplane"
        message={
          error instanceof Error
            ? error.message
            : "Something went wrong while loading Fiberplane."
        }
      />
    );
  },
});

function RootComponent() {
  return (
    <div className="h-full">
      <Outlet />
      <Toaster />
      {/*  Commented out because they're annoying but leaving them here in case you need them */}
      {/* <TanStackRouterDevtools position="bottom-right" /> */}
      {/* <ReactQueryDevtools /> */}
    </div>
  );
}

// NOTE - Only exported to avoid typescript errors during compilation when this is commented out
export const TanStackRouterDevtools: React.LazyExoticComponent<React.ComponentType> =
  React.lazy(() =>
    // Lazy load in development
    import("@tanstack/router-devtools").then((res) => ({
      default: res.TanStackRouterDevtools,
      // For Embedded Mode
      // default: res.TanStackRouterDevtoolsPanel
    })),
  );

// NOTE - Only exported to avoid typescript errors during compilation when this is commented out
export const ReactQueryDevtools: React.LazyExoticComponent<React.ComponentType> =
  React.lazy(() =>
    // Lazy load in development
    import("@tanstack/react-query-devtools/build/modern/production.js").then(
      (res) => ({
        default: res.ReactQueryDevtools,
      }),
    ),
  );
